import { IImageData, IImageSettings, Image, RichTextComponent } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IHomeCarouselProps, IHomeCarouselSlidesData, ILinkData } from './home-carousel.props.autogenerated';

/**
 *
 * HomeCarousel component
 * @extends {React.Component<IHomeCarouselProps<{}>>}
 */
@observer
class HomeCarousel extends React.Component<IHomeCarouselProps<{}>> {

    //==========================================================================
    // VARIABLES
    //==========================================================================

    @observable private currentSlide: number;
    private slides: IHomeCarouselSlidesData[];
    private scrollThreshold: number;
    private scrollStart: number | undefined;
    private cycleInterval?: ReturnType<typeof setInterval>;
    private defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: `w=24&h=24&m=6`, w: 0, h: 0 },
            lg: { q: `w=24&h=24&m=6`, w: 0, h: 0 }
        },
        lazyload: true
    };

    //==========================================================================
    // LIFE CYCLE
    //==========================================================================

    //------------------------------------------------------
    //------------------------------------------------------
    constructor(props: IHomeCarouselProps<{}>) {
        super(props);
        this.currentSlide = 0;
        this.slides = props.config.homeCarouselSlides ? props.config.homeCarouselSlides.filter(slide => (!slide.hidden)) : [];
        this.scrollThreshold = 100;
        this._randomizeStart = this._randomizeStart.bind(this);
        this._autoAdvance = this._autoAdvance.bind(this);
        this._restartAutoAdvance = this._restartAutoAdvance.bind(this);
        this._previousSlide = this._previousSlide.bind(this);
        this._nextSlide = this._nextSlide.bind(this);
        this._handleTouchStart = this._handleTouchStart.bind(this);
        this._handleTouchEnd = this._handleTouchEnd.bind(this);
        this._handleKeyPress = this._handleKeyPress.bind(this);
        this._selectorSlideChange = this._selectorSlideChange.bind(this);
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public componentDidMount(): void {
        document.addEventListener('keyup', this._handleKeyPress);

        if (this.props.config.homeCarouselRandomize && this.slides) {
            this._randomizeStart(this.slides.length);
        }

        if (this.props.config.homeCarouselInterval && this.slides) {
            this._autoAdvance();
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public componentWillUnmount(): void {
        document.removeEventListener('keyup', this._handleKeyPress);
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public render(): JSX.Element | null {
        if (!this.slides) {
            return null;
        }
        return this._getCarousel;
    }

    //==========================================================================
    // RENDER METHODS
    //==========================================================================

    //------------------------------------------------------
    //------------------------------------------------------
    private get _getCarousel(): JSX.Element {
        const currentClassName = this.slides && this.slides[this.currentSlide].className;
        const selectorList = [] as JSX.Element[];
        const indicatorList = [] as JSX.Element[];
        const slideList = [] as JSX.Element[];
        this.slides.map((slide, index) => {
            selectorList.push(
                this._getSelector(slide, index)
            );
            indicatorList.push(
                this._getIndicator(index)
            );
            slideList.push(
                this._getSlide(slide, index)
            );
        });
        return (
            <div
                className={classnames('home-carousel', this.props.config.className, currentClassName)}
                onTouchStart={this._handleTouchStart}
                onTouchEnd={this._handleTouchEnd}
            >
                <div className='home-carousel__selector-container'>
                    {selectorList}
                    {this._getNext(this.props.resources.homeCarousel_next)}
                </div>
                <div className='home-carousel__slide-container'>
                    {slideList}
                </div>
                <div className='home-carousel__indicator-container'>
                    {indicatorList}
                </div>
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getSelector(slide: IHomeCarouselSlidesData, index: number): JSX.Element {
        const isCurrentSelector = index === this.currentSlide;
        return (
            <div
                className={classnames('home-carousel__selector', { 'home-carousel__selector-current': isCurrentSelector })}
                role='button'
                onClick={this._selectorSlideChange}
                data-id={index}
                key={index}
            >
                <div className='home-carousel__selector-link'>{slide.slideSelector}</div>
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getNext(text: string): JSX.Element {
        return (
            <div
                className='home-carousel__next'
                role='button'
                onClick={this._nextSlide}
            >
                <div className='home-carousel__next-icon' />
                <div className='home-carousel__next-text'>{text}</div>
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getIndicator(index: number): JSX.Element {
        const isCurrentIndicator = index === this.currentSlide;
        return (
            <div
                className={classnames('home-carousel__indicator', { 'home-carousel__indicator-current': isCurrentIndicator })}
                role='button'
                onClick={this._selectorSlideChange}
                data-id={index}
                key={index}
            />
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getSlide(slide: IHomeCarouselSlidesData, index: number): JSX.Element {
        const isCurrentSlide = index === this.currentSlide;
        return (
            <a
                className={classnames('home-carousel__slide', { 'home-carousel__slide-current': isCurrentSlide }, slide.className)}
                href={slide.link?.linkUrl.destinationUrl}
                aria-label={slide.link?.ariaLabel}
                target={slide.link?.openInNewTab ? '_blank' : undefined} rel="noreferrer"
            >
                <div className='home-carousel__slide-desktop'>
                    <div className='home-carousel__slide-content'>
                        {this._getLogo(slide.logo)}
                        <RichTextComponent text={slide.text || ''} className='home-carousel__slide-text' />
                        {this._getLink(slide.link)}
                    </div>
                    <div className='home-carousel__slide-background'>
                        {this._getBackgroundImage(slide.backgroundImage)}
                    </div>
                </div>
                <div className='home-carousel__slide-mobile'>
                    <div className='home-carousel__slide-content'>
                        {this._getLogo(slide.logoMobile || slide.logo)}
                    </div>
                    <div className='home-carousel__slide-background'>
                        {this._getBackgroundImage(slide.backgroundImageMobile)}
                    </div>
                </div>
            </a>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getLink(link: ILinkData | undefined): JSX.Element | null {
        if (link && link.linkUrl.destinationUrl && link.linkText) {
            return (
                <a
                    className='home-carousel__slide-link'
                    href={link.linkUrl.destinationUrl}
                    aria-label={link.ariaLabel}
                    target={link.openInNewTab ? '_blank' : undefined} rel="noreferrer"
                >
                    {link.linkText}
                </a>
            );
        } else {
            return null;
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getLogo(image: IImageData | undefined): JSX.Element | null {
        if (image) {
            return (
                <div className='home-carousel__slide-logo'>
                    <Image
                        {...image}
                        gridSettings={this.props.context.request.gridSettings!}
                        imageSettings={image.imageSettings || this.defaultImageSettings}
                    />
                </div>
            );
        }
        return null;
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _getBackgroundImage(image: IImageData | undefined): JSX.Element | null {
        if (image) {
            return (
                <div className='home-carousel__slide-background-image'>
                    <Image
                        {...image}
                        gridSettings={this.props.context.request.gridSettings!}
                        imageSettings={image.imageSettings || this.defaultImageSettings}
                    />
                </div>
            );
        }
        return null;
    }

    //==========================================================================
    // HANDLE METHODS
    //==========================================================================

    //------------------------------------------------------
    //------------------------------------------------------
    private _randomizeStart(max: number): void {
        // tslint:disable-next-line:insecure-random
        const startSlide: number = Math.round(Math.random() * (max - 1));
        this.currentSlide = startSlide;
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _autoAdvance(): void {
        const { homeCarouselInterval } = this.props.config;
        if (homeCarouselInterval && this.slides) {
            this.cycleInterval = setInterval(this._nextSlide, homeCarouselInterval);
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _restartAutoAdvance(): void {
        if (this.cycleInterval) {
            clearInterval(this.cycleInterval);
            this._autoAdvance();
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _selectorSlideChange(element: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
        const index = element.currentTarget.getAttribute('data-id')!;
        this.currentSlide = +index;
        this._restartAutoAdvance();
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _previousSlide(): void {
        this.currentSlide = (this.currentSlide > 0) ? this.currentSlide - 1 : this.slides.length - 1;
        this._restartAutoAdvance();
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _nextSlide(): void {
        this.currentSlide = (this.currentSlide < this.slides.length - 1) ? this.currentSlide + 1 : 0;
        this._restartAutoAdvance();
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _handleTouchStart(event: React.TouchEvent<HTMLDivElement>): void {
        if (event.touches.length === 0) {
            this.scrollStart = undefined;
        } else {
            this.scrollStart = event.touches[0].screenX;
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _handleTouchEnd(event: React.TouchEvent<HTMLDivElement>): void {
        if (event.changedTouches.length > 0 && this.scrollStart !== undefined) {
            const newTarget: number = event.changedTouches[0].screenX;
            const delta = newTarget - this.scrollStart;

            if (delta > this.scrollThreshold) {
                this._previousSlide();
            }

            if (delta < -this.scrollThreshold) {
                this._nextSlide();
            }
        }

        this.scrollStart = undefined;

        return;
    }

    //------------------------------------------------------
    //------------------------------------------------------
    // tslint:disable-next-line:no-any
    private _handleKeyPress = (event: any) => {
        if (event.keyCode === 37) {
            this._previousSlide();
        } else if (event.keyCode === 39) {
            this._nextSlide();
        }
    };
}

export default HomeCarousel;
